import React from "react";
import { Helmet } from "react-helmet-async";
import "./RemoteCache.css"
import configureStoreImg from "../../assets/images/Blogs/ConfigureStoreImage/configureStore.PNG";
import configureimage1 from "../../assets/images/Blogs/ConfigureStoreImage/configure1.PNG";
import configureimage2 from "../../assets/images/Blogs/ConfigureStoreImage/configure2.PNG";
import configureimage3 from "../../assets/images/Blogs/ConfigureStoreImage/configure3.PNG";
import step2img1 from "../../assets/images/Blogs/ConfigureStoreImage/step2image1.PNG";
import step2img2 from "../../assets/images/Blogs/ConfigureStoreImage/step2image2.PNG";
import step2img3 from "../../assets/images/Blogs/ConfigureStoreImage/step2image3.PNG";
import step2img4 from "../../assets/images/Blogs/ConfigureStoreImage/step2image4.PNG";
import step3img1 from "../../assets/images/Blogs/ConfigureStoreImage/step3image1.PNG";
import step3img2 from "../../assets/images/Blogs/ConfigureStoreImage/step3image2.PNG";
import step3img3 from "../../assets/images/Blogs/ConfigureStoreImage/step3image3.PNG";
import step3img4 from "../../assets/images/Blogs/ConfigureStoreImage/step3image4.PNG";
import step3img5 from "../../assets/images/Blogs/ConfigureStoreImage/step3image5.PNG";
import step3img6 from "../../assets/images/Blogs/ConfigureStoreImage/step3image6.PNG";

const RemoteCache = () => {
  return (
    <>
      <Helmet>
        <title>Configure Teamcenter Store and Forward for Remote Cache | NexTech PLM</title>
        <meta name="description" content="Step-by-step guide to configure Teamcenter store and forward for remote cache users. Learn FMS configuration, Data Share Manager setup, and faster large-file transfers." />
        <meta name="keywords" content="Teamcenter store and forward, FMS remote cache, Teamcenter file transfer, store forward configuration, Data Share Manager, remote FSC server" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://nextechplm.com/blog/Configure-Store-and-Forward" />
        <meta property="og:site_name" content="NexTech PLM Solutions" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Configure Teamcenter Store and Forward for Remote Cache | NexTech PLM" />
        <meta property="og:description" content="Step-by-step guide to configure Teamcenter store and forward for remote cache users. Learn FMS configuration, Data Share Manager setup, and faster large-file transfers." />
        <meta property="og:url" content="https://nextechplm.com/blog/Configure-Store-and-Forward" />
        <meta property="og:image" content="https://nextechplm.com/logo192.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Configure Teamcenter Store and Forward for Remote Cache | NexTech PLM" />
        <meta name="twitter:description" content="Step-by-step guide to configure Teamcenter store and forward for remote cache users. Learn FMS configuration, Data Share Manager setup, and faster large-file transfers." />
        <meta name="twitter:image" content="https://nextechplm.com/logo192.png" />
      </Helmet>
      <section className="banner-audit">
        <h2 className="banner-title">
          How to configure store and forward for a Remote cache_ver2{" "}
        </h2>
      </section>

      <section className="content-section container">
      <div className="content-text">
        <p className="intro-paragraph">
          This article focuses on how to upload or download files using Data Share Manager asynchronously while also applying file upload security policies.
        </p>

        <blockquote className="highlighted-text">
          Teamcenter Store and Forward Configuration for Remote Users – 
          <span className="highlight">Closer the Volume → Faster the Upload → Improved User Efficiency!</span>
        </blockquote>

        <p>
          In today's global operations, sharing, collaborating, and exchanging high volumes of business data (including large CAD and non-CAD files) in a complex technical environment using PLM technology is critical. Ideally, users uploading files to the PLM system should experience low latency to meet network constraints and reduce bandwidth consumption during peak hours between company locations.
        </p>

        <p>
          To address this, Teamcenter provides an FMS Store and Forward mechanism, enabling remote users to upload files to a local file storage (referred to as a temporary or store-and-forward volume). This local volume stores production data locally before it is automatically transferred to the final destination across the Wide Area Network (WAN) via scheduled tasks. Until the transfer occurs, remote users downloading the file can access it from the default local volume on their LAN, significantly improving upload times.
        </p>

        <p className="conclusion">
          <strong>
            This blog will guide you through the different aspects of store-and-forward installation, configuration, and usage with a working example!
          </strong>
        </p>
      </div>

      <div className="image-container">
        <img src={configureStoreImg} alt="Configure store illustration" className="responsive-img" />
      </div>
    </section>

   <section className="container store-forward-section">
      <h3 className="section-heading">
        Working Example of Store and Forward Installation and Configuration
      </h3>
      <p>
        <strong>Assumption: </strong>
        As shown in the picture above, this example assumes you have a corporate server with different features installed, and you now want to set up a remote volume (store and forward volume) server for remote site users.
      </p>

      <h4>Step 1: Configure Remote FSC Server</h4>
      <ul className="audit-log-list">
        <li>Run `tem.bat` from the kit and select volume server as shown below.</li>
        <img src={configureimage1} alt="Configure remote FSC server screen" className="step-image" />
        <li>On the Features screen, ensure that only the FMS Server Cache feature is selected, and no other features.</li>
        <img src={configureimage2} alt="FSC feature selection screen" className="step-image" />
        <li>Provide the FSC parent URL (from the corporate server FMS) and define the FSC ID for the remote server.</li>
        <img src={configureimage3} alt="Remote FSC URL setup screen" className="step-image" />
        <li>Click next and complete the installation.</li>
      </ul>

      <h4>Step 2: Configure Master FSC Server on Corporate Server</h4>
      <ul className="audit-log-list">
        <li>Login as `infodba` to RAC.</li>
        <li>Create a new volume, as shown below, using the remote FSC ID.</li>
        <img src={step2img1} alt="Remote volume configuration" className="step-image" />
        <li>Launch `tem.bat` from the TC_ROOT/install directory.</li>
        <li>Select "Update FMS Master".</li>
        <img src={step2img2} alt="Feature maintenance step" className="step-image" />
        <li>Add the new FSC (Remote FSC) by selecting "Add" on the FSC Servers screen.</li>
        <img src={step2img3} alt="FSC server addition step" className="step-image" />
        <li>Perform a volume query scan, and you should see two different volumes.</li>
        <img src={step2img4} alt="Volume query result" className="step-image" />
        <li>Click next and finish the installation. Restart FSC1 followed by FSC2.</li>
      </ul>

      <h4>Step 3: Configure Store and Forward</h4>
      <ul className="audit-log-list">
        <li>
          <strong>On corporate server: </strong>Install Dispatcher Server, Module, Scheduler, and Admin Client. If already installed, proceed to the next step.
        </li>
        <li>Launch `tem.bat` and select the StoreAndForward translator on the selection translator page.</li>
        <img src={step3img1} alt="Store and forward translator selection" className="step-image" />
        <li>Provide the master FSC Server URL as shown below for store-forward-translator.</li>
        <img src={step3img2} alt="Master FSC server URL setup" className="step-image" />
        <li>Complete the installation by clicking next.</li>
        <li>
          Log in with `infodba` and set the following preferences:
          <ul>
            <li>`TC_Store_and_Forward` preference to true.</li>
            <li>`FMS_SAF_Batch_Transfer_Enabled` to true.</li>
          </ul>
        </li>
        <li>Set a local volume for required users.</li>
        <img src={step3img3} alt="Local volume assignment step" className="step-image" />
        <li>
          Test the remote volume by creating a dataset with a named reference file and ensuring it gets stored in the `remote_volume`.
        </li>
        <li>
          Sync the contents from `remote_volume` with the master volume on the corporate server by running the store and forward translator in Dispatcher Admin Client as a repeating job.
          <ul>
            <li>Run `runUI.bat` from the dispatcher admin client directory `D:\apps\TC\DS\AdminClient\bin`.</li>
            <li>Log in as `infodba`.</li>
            <img src={step3img4} alt="Dispatcher admin client login" className="step-image" />
            <li>Schedule translation tasks as shown below based on your requirements.</li>
            <img src={step3img5} alt="Translation task scheduling" className="step-image" />
          </ul>
        </li>
        <li>Alternatively, sync by creating a cron job to run `move_volume_files` with the `transferaf` argument.</li>
        <img src={step3img6} alt="Move volume files command example" className="step-image" />
        <li>
          Either of the last two steps will move the files from the `remote_volume` to the master volume on the corporate server.
        </li>
      </ul>

      <h4>Business Benefits of Using Store-and-Forward</h4>
      <ul className="benefits-list">
        <li>Better file upload performance for remote site users.</li>
        <li>Master volume stays up-to-date with data from remote sites.</li>
      </ul>
    </section>

    </>
  );
};

export default RemoteCache;
